/**
 * Unified API Client for WWP Dashboard
 * Handles all HTTP requests with standardized error handling
 */

import merge from 'lodash/merge';
import { store } from '../redux/configureStore';
import { toastService } from '../services/ToastService/toastService';
import { isDevelopment } from '../helpers/utilityFunctions/utilityFunctions';
import {
  ApiRequestOptions,
  BusinessError,
  isWwpError,
  NetworkError,
  ValidationError,
  WwpErrorResponse,
} from './types';
import { getErrorMessage } from './errorCodes';

const baseUrl = import.meta.env.VITE_APP_API_URL;

/**
 * Get the current JWT token from Redux store
 */
function getToken(): string | null {
  const state = store.getState();
  return state.account.account.jwtToken;
}

/**
 * Parse response body as JSON
 */
async function parseResponseBody(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.indexOf('json') >= 0) {
    const text = await response.text();
    if (!text) return null;
    
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`Failed to parse JSON response: ${text}`);
    }
  }
  
  return await response.text();
}

/**
 * Handle API response and throw appropriate errors
 */
async function handleApiResponse<T>(
  response: Response,
  skipErrorToast: boolean = false
): Promise<T> {
  const body = await parseResponseBody(response);

  // Check if response is OK (2xx status)
  if (response.ok) {
    return body as T;
  }

  // Check if it's a standardized WwpErrorResponse
  if (isWwpError(body)) {
    // Handle validation errors
    if (body.errors && body.errors.length > 0) {
      const error = new ValidationError(body.message, body.errors);
      
      if (isDevelopment() && !skipErrorToast) {
        const errorMessages = body.errors
          .map((e) => `${e.key}: ${e.values.join(', ')}`)
          .join('; ');
        toastService.showErrorToast(`${body.message}: ${errorMessages}`);
      }
      
      throw error;
    }

    // Handle business logic errors
    const errorMessage = body.message || getErrorMessage(body.code);
    const error = new BusinessError(
      errorMessage,
      body.code,
      body.longCode,
      body.status
    );

    if (isDevelopment() && !skipErrorToast) {
      toastService.showErrorToast(`Error ${body.code}: ${errorMessage}`);
    }

    throw error;
  }

  // Handle old-style error responses (for backward compatibility)
  if (body && typeof body === 'object' && 'errors' in body) {
    const errorMessage = body.title || 'Validation error';
    const errors = Object.entries(body.errors || {}).map(([key, values]) => ({
      key,
      values: values as string[],
    }));

    if (errors.length > 0) {
      throw new ValidationError(errorMessage, errors);
    }
  }

  // Handle HTTP errors without structured error response
  const statusText = response.statusText || 'Unknown error';
  const errorMessage = `HTTP ${response.status}: ${statusText}`;

  if (isDevelopment() && !skipErrorToast) {
    toastService.showErrorToast(errorMessage);
  }

  // Throw BusinessError so token refresh can detect 401 errors
  throw new BusinessError(errorMessage, '', '', response.status);
}

/**
 * Make an API request with automatic token injection and error handling
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { skipAuth = false, skipErrorToast = false, ...fetchOptions } = options;

  // Build full URL
  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;

  // Prepare headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string> | undefined),
  };

  // Add Authorization header if not skipped and token exists
  if (!skipAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Merge options
  const finalOptions: RequestInit = merge({}, fetchOptions, { headers });

  try {
    const response = await fetch(url, finalOptions);
    return await handleApiResponse<T>(response, skipErrorToast);
  } catch (error) {
    // Re-throw our custom errors
    if (
      error instanceof BusinessError ||
      error instanceof ValidationError ||
      error instanceof NetworkError
    ) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new NetworkError();
    }

    // Re-throw other errors
    throw error;
  }
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

export type { ApiRequestOptions } from './types';

